import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

// Higher-order component for route protection
export const withAuth = (WrappedComponent, options = {}) => {
  const {
    requireAdmin: needAdmin = false,
    requireLineUser: needLineUser = false,
    redirectTo = '/login',
    fallbackComponent = null
  } = options;

  return function AuthenticatedRoute(props) {
    const { isAuthenticated, loading, requireAuth, requireAdmin: checkAdmin, requireLineUser: checkLineUser } = useAuth();
    const location = useLocation();

    if (loading) {
      return fallbackComponent || <LoadingSpinner />;
    }

    if (!requireAuth()) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    if (needAdmin && !checkAdmin()) {
      return <Navigate to="/admin-login" state={{ from: location }} replace />;
    }

    if (needLineUser && !checkLineUser()) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <WrappedComponent {...props} />;
  };
};

// Middleware hook for manual authentication checks
export const useAuthMiddleware = () => {
  const { requireAuth, requireAdmin, requireLineUser, user } = useAuth();
  const location = useLocation();

  const checkAuth = (options = {}) => {
    const { adminOnly = false, lineOnly = false } = options;

    if (!requireAuth()) {
      return { authenticated: false, redirectTo: '/login' };
    }

    if (adminOnly && !requireAdmin()) {
      return { authenticated: false, redirectTo: '/admin-login' };
    }

    if (lineOnly && !requireLineUser()) {
      return { authenticated: false, redirectTo: '/login' };
    }

    return { authenticated: true, redirectTo: null };
  };

  const canAccess = (resource) => {
    if (!user) return false;

    // Check based on user role and resource requirements
    const resourcePermissions = {
      'admin': user.isAdmin, // Only true admins, not managers
      'agents': user.isAdmin || user.isManager || user.role === 'manager',
      'policies': user.isAdmin || user.isManager || user.role === 'manager' || user.role === 'agent',
      'performance': user.isAdmin || user.isManager || user.role === 'manager' || user.role === 'agent',
      'profile': true,
      'dashboard': true
    };

    return resourcePermissions[resource] || false;
  };

  return {
    checkAuth,
    canAccess,
    currentUser: user,
    isAuthenticated: !!user
  };
};

// Route guard component for admin routes
export const AdminRoute = ({ children }) => {
  const AdminComponent = withAuth(() => children, { 
    requireAdmin: true, 
    redirectTo: '/admin-login' 
  });
  return <AdminComponent />;
};

// Route guard component for LINE user routes
export const LineRoute = ({ children }) => {
  const LineComponent = withAuth(() => children, { 
    requireLineUser: true, 
    redirectTo: '/login' 
  });
  return <LineComponent />;
};

// Generic protected route
export const ProtectedRoute = ({ children, adminOnly = false, lineOnly = false }) => {
  const ProtectedComponent = withAuth(() => children, { 
    requireAdmin: adminOnly,
    requireLineUser: lineOnly,
    redirectTo: adminOnly ? '/admin-login' : '/login'
  });
  return <ProtectedComponent />;
};
